import React from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";

// OptionGroup Component accepts a maxVisible prop for mobile extra options.
const OptionGroup = ({ options, selectedCount, onClick, maxVisible = 3 }) => {
  // For mobile, split options into visible ones and extras.
  const visibleOptions = options.slice(0, maxVisible);
  const extraOptions = options.slice(maxVisible);

  return (
    <>
      {/* Desktop / Tablet Version: All options shown in a horizontal inline-flex layout */}
      <div className="hidden sm:inline-flex w-fit h-fit flex-shrink-0 flex-grow-0 items-center border-[1.6px] border-secondary/40 rounded-lg overflow-hidden drop-shadow-sm">
        <div className="px-3 py-1 text-secondary/80 text-body1 border-r font-semibold border-secondary/40">
          {selectedCount} Selected
        </div>
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => onClick(option.value)}
            className="px-3 py-1 flex items-center gap-1 text-secondary/90 text-body1 border-r border-secondary/40 hover:bg-secondary/95 hover:text-background/90 last:border-r-0 transition-all ease-in-out duration-150"
          >
            {option.icon && <span className="text-lg">{option.icon}</span>}
            {option.label}
          </button>
        ))}
      </div>

      {/* Mobile Version: Compact layout with a "More" button if there are extra options */}
      <div className="sm:hidden w-fit flex flex-col  ">
        {/* Selected count chip */}
        <div className="px-1 w-fit py-1 text-secondary/80 font-semibold text-body1">
          {selectedCount} Selected
        </div>
        <div className="sm:hidden  w-full inline-flex items-center border-[1.6px] border-secondary/40 rounded-lg overflow-hidden drop-shadow-sm">
          {/* Visible option buttons */}
          {visibleOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => onClick(option.value)}
              className="px-2 py-2 flex items-center shrink-0 gap-1 h-full text-secondary/90 text-body2 border-r last:border-r-0 border-secondary/40 hover:bg-secondary/95 hover:text-background/90 transition-all ease-in-out duration-150"
            >
              {option.icon && <span className="text-body2">{option.icon}</span>}
              {option.label}
            </button>
          ))}
          {/* "More" button appears only if there are extra options */}
          {extraOptions.length > 0 && (
            <MobileMoreButton extraOptions={extraOptions} onClick={onClick} />
          )}
        </div>
      </div>
    </>
  );
};

// MobileMoreButton renders a nicely styled popover for extra options.
const MobileMoreButton = ({ extraOptions, onClick }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="px-2 py-1 flex h-full items-center justify-center gap-1 text-secondary text-body2 hover:bg-secondary/20 transition">
          More
          <ChevronDownIcon className="h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-full bg-background border border-secondary/60 rounded-lg shadow-lg">
        <div className="flex flex-col gap-1">
          {extraOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => onClick(option.value)}
              className="px-2 py-1 flex items-center gap-1 text-secondary text-body2 hover:bg-secondary/20 transition rounded"
            >
              {option.icon && <span className="text-body2">{option.icon}</span>}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OptionGroup;
